import React, { createContext, useContext, useState, useCallback, ReactNode } from "react"
import { MENU_SECTIONS } from "src/components/Sidebar/constants"

type SidebarContextType = {
  isSidebarVisible: boolean
  setIsSidebarVisible: React.Dispatch<React.SetStateAction<boolean>>
  toggleSidebarVisibility: () => void
  openSections: Record<string, boolean>
  setOpenSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  toggleSection: (title: string) => void
}

const defaultOpenSections = MENU_SECTIONS.reduce(
  (acc, section) => {
    acc[section.title] = true
    return acc
  },
  {} as Record<string, boolean>
)

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(defaultOpenSections)

  const toggleSidebarVisibility = useCallback(() => {
    setIsSidebarVisible((prev) => !prev)
  }, [])

  const toggleSection = useCallback((title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }))
  }, [])

  return (
    <SidebarContext.Provider
      value={{
        isSidebarVisible,
        setIsSidebarVisible,
        toggleSidebarVisibility,
        openSections,
        setOpenSections,
        toggleSection,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export const useSidebarContext = (): SidebarContextType => {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error("useSidebarContext must be used within a SidebarProvider")
  }
  return context
}
